import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UploadSimple, FileArrowUp, MagnifyingGlass, Sparkle, Trash, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

const STATUS_STYLES = {
  draft: { label: "Draft", cls: "border-stone-300 bg-stone-100 text-stone-500" },
  published: { label: "Published", cls: "border-[#27AE60]/30 bg-[#27AE60]/10 text-[#1e8449]" },
  archived: { label: "Archived", cls: "border-[#C0392B]/30 bg-[#C0392B]/10 text-[#C0392B]" },
};

const MY_SIGNOFF = {
  due: { label: "Sign-off due", cls: "border-[#C0392B]/30 bg-[#C0392B]/10 text-[#C0392B]" },
  stale: { label: "Re-sign needed — policy changed", cls: "border-[#B7791F]/30 bg-[#B7791F]/10 text-[#8a5d14]" },
  current: { label: "Signed", cls: "border-[#27AE60]/30 bg-[#27AE60]/10 text-[#1e8449]" },
};

const emptyQuestion = () => ({ q: "", choices: ["", "", "", ""], answer_index: 0, explanation: "" });

export default function PoliciesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin" && sessionStorage.getItem("shc_view_staff") !== "1";
  const [policies, setPolicies] = useState(null);
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState("published");
  const [editing, setEditing] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [versions, setVersions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [qaQuestion, setQaQuestion] = useState("");
  const [qaAnswer, setQaAnswer] = useState("");
  const [qaBusy, setQaBusy] = useState(false);

  const [settingsFor, setSettingsFor] = useState(null);
  const [settings, setSettings] = useState(null);
  const [settingsBusy, setSettingsBusy] = useState("");
  const [staff, setStaff] = useState([]);
  const [rosterFor, setRosterFor] = useState(null);
  const [roster, setRoster] = useState(null);
  const [required, setRequired] = useState(null);
  const [nbHistory, setNbHistory] = useState(null);
  const [nbOpen, setNbOpen] = useState("");

  const toggleHistory = async (p) => {
    if (historyFor === p.id) { setHistoryFor(null); return; }
    try {
      const { data } = await api.get(`/policies/${p.id}/versions`);
      setVersions(data.versions || []);
      setHistoryFor(p.id);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const askPolicy = async () => {
    if (!qaQuestion.trim()) return;
    setQaBusy(true);
    setQaAnswer("");
    try {
      const { data } = await api.post("/policies/ask", { question: qaQuestion.trim() }, { timeout: 90000 });
      setQaAnswer(data.answer);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setQaBusy(false);
    }
  };

  const load = () => api.get("/policies").then((r) => setPolicies(r.data)).catch((e) => toast.error(formatApiError(e)));
  const loadRequired = () => {
    if (isAdmin) api.get("/policies/required-coverage").then((r) => setRequired(r.data.items || [])).catch(() => {});
  };
  const loadNbHistory = () => {
    if (isAdmin) api.get("/reports/policy-notebook/history").then((r) => setNbHistory(r.data.history || [])).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); loadRequired(); loadNbHistory(); }, []);

  useEffect(() => {
    if (isAdmin && policies && policies.length && !policies.some((p) => p.status === "published")) setTab("draft");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policies === null]);

  const visible = useMemo(() => {
    let list = policies || [];
    if (isAdmin && tab !== "all") list = list.filter((p) => p.status === tab);
    const q = query.trim().toLowerCase();
    if (q) list = list.filter((p) => `${p.title} ${p.category} ${(p.tags || []).join(" ")} ${p.content}`.toLowerCase().includes(q));
    return list;
  }, [policies, tab, query, isAdmin]);

  const counts = useMemo(() => {
    const c = { all: 0, draft: 0, published: 0, archived: 0 };
    for (const p of policies || []) { c.all += 1; c[p.status] = (c[p.status] || 0) + 1; }
    return c;
  }, [policies]);

  const myDue = useMemo(() => (policies || []).filter((p) => p.require_signoff && (p.my_signoff_status === "due" || p.my_signoff_status === "stale")), [policies]);

  const save = async () => {
    if (!editing.title.trim() || !editing.content.trim()) { toast.error("Title and content are required"); return; }
    setBusy(true);
    try {
      const payload = { title: editing.title.trim(), content: editing.content.trim(), category: (editing.category || "").trim(),
        tags: (editing.tags || "").split(",").map((t) => t.trim()).filter(Boolean) };
      if (editing.id) {
        const { data } = await api.put(`/policies/${editing.id}`, { ...payload, require_reattestation: !!editing.reattest });
        toast.success(data.version > editing.version
          ? `Saved as version ${data.version}${editing.reattest ? " — staff must re-acknowledge" : ""}`
          : "Saved");
      } else {
        await api.post("/policies", payload);
        toast.success("Policy created as a draft — publish it when ready");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (p, status) => {
    setBusy(true);
    try {
      await api.post(`/policies/${p.id}/status`, { status });
      toast.success(status === "published" ? "Published — staff can now read this policy" : status === "archived" ? "Archived" : "Moved back to draft");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Permanently delete "${p.title}"?`)) return;
    try {
      await api.delete(`/policies/${p.id}`);
      toast.success("Policy deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const createRequired = async (r) => {
    setBusy(`req-${r.key}`);
    try {
      await api.post("/policies", {
        title: r.title,
        content: `Draft policy: ${r.title}\n\nRequired by: ${r.required_by.join(", ")}.\n\n[Write the policy here — then run it through Knowledge Base → Policy Check to verify it against your sources.]`,
        category: "Compliance",
      });
      toast.success(`Draft created for "${r.title}" — edit, publish, then run Policy Check`);
      load();
      loadRequired();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const doImport = async () => {
    if (!importFile) { toast.error("Choose a file first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const { data } = await api.post("/policies/import", fd, { timeout: 60000 });
      toast.success(`Imported "${data.title}" as a draft`);
      setImportOpen(false);
      setImportFile(null);
      setTab("draft");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const openSettings = async (p) => {
    setSettingsFor(p);
    setSettings({
      require_signoff: !!p.require_signoff,
      include_in_orientation: !!p.include_in_orientation,
      assigned_all: !p.assigned_to?.length || p.assigned_to.includes("all"),
      assigned_ids: p.assigned_to?.includes("all") ? [] : (p.assigned_to || []),
      quiz: (p.quiz || []).map((q) => ({ ...q, choices: [...(q.choices || [])] })),
    });
    if (!staff.length) {
      try {
        const { data } = await api.get("/users");
        setStaff((data || []).filter((u) => u.role !== "admin" && u.active !== false));
      } catch (e) { console.debug("staff list unavailable for assignment picker:", e); }
    }
  };

  const saveSettings = async () => {
    setSettingsBusy("save");
    try {
      await api.put(`/policies/${settingsFor.id}/settings`, {
        require_signoff: settings.require_signoff,
        include_in_orientation: settings.include_in_orientation,
        assigned_to: settings.assigned_all ? ["all"] : settings.assigned_ids,
        quiz: settings.quiz,
      });
      toast.success("Sign-off settings saved");
      setSettingsFor(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSettingsBusy("");
    }
  };

  const generateQuiz = async () => {
    setSettingsBusy("gen");
    try {
      const { data } = await api.post(`/policies/${settingsFor.id}/quiz/generate`, {}, { timeout: 120000 });
      setSettings((s) => ({ ...s, quiz: data.questions || [] }));
      toast.success(`AI drafted ${data.questions?.length || 0} questions — review and edit before saving`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSettingsBusy("");
    }
  };

  const setQuestion = (i, patch) => setSettings((s) => ({ ...s, quiz: s.quiz.map((q, idx) => (idx === i ? { ...q, ...patch } : q)) }));

  const openRoster = async (p) => {
    setRosterFor(p);
    setRoster(null);
    try {
      const { data } = await api.get(`/policies/${p.id}/signoffs`);
      setRoster(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setRosterFor(null);
    }
  };

  const openViewing = (p) => navigate(`/policies/${p.id}`);

  if (!policies) {
    return <div className="flex justify-center py-24"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;
  }

  return (
    <div className="max-w-4xl" data-testid="policies-page">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-4xl font-semibold text-stone-900">Policies</h1>
          <p className="mt-1 text-sm text-stone-500">
            {isAdmin
              ? "Your agency policy library. Drafts are private to admins — publish a policy to make it readable by all staff."
              : "Published agency policies. Read any policy below."}
          </p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => window.open(`${api.defaults.baseURL}/policies/export/docx`, "_blank")}
              className="border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white" data-testid="policy-export-docx">
              Download Final policies (editable)
            </Button>
            <Button variant="outline" onClick={() => window.open(`${api.defaults.baseURL}/reports/policy-notebook`, "_blank")}
              className="border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white" data-testid="policy-notebook-button">
              Policy Notebook
            </Button>
            <Button variant="outline" onClick={() => window.open(`${api.defaults.baseURL}/reports/survey-packet`, "_blank")}
              className="border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white" data-testid="survey-packet-button">
              Survey Packet
            </Button>
            <Button variant="outline" onClick={() => setImportOpen(true)}
              className="border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white" data-testid="policy-import-button">
              <UploadSimple size={16} className="mr-1.5" /> Import Document
            </Button>
            <Button onClick={() => setEditing({ title: "", content: "", category: "", version: 1 })}
              className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="new-policy-button">
              + New Policy
            </Button>
          </div>
        )}
      </div>

      {isAdmin && (nbHistory || []).length > 0 && (
        <div className="mb-5 rounded-lg border border-stone-200 bg-white p-4 shadow-sm" data-testid="notebook-history">
          <p className="text-xs font-semibold uppercase tracking-wide text-stone-400">Policy notebook history — exactly which policy versions were current on each date</p>
          <div className="mt-2 space-y-1.5">
            {nbHistory.map((n) => (
              <div key={n.id} className="rounded-md border border-stone-100" data-testid={`notebook-entry-${n.id}`}>
                <button type="button" onClick={() => setNbOpen((cur) => (cur === n.id ? "" : n.id))}
                  className="flex w-full flex-wrap items-center gap-2 px-3 py-2 text-left" data-testid={`notebook-toggle-${n.id}`}>
                  <span className="text-sm font-medium text-stone-800">{String(n.at || "").slice(0, 10)}</span>
                  <span className="text-xs text-stone-400">{String(n.at || "").slice(11, 16)} · {n.by} · {n.policy_count} policies</span>
                  <span className="ml-auto text-xs font-medium text-primary">{nbOpen === n.id ? "Hide manifest" : "Show manifest"}</span>
                </button>
                {nbOpen === n.id && (
                  <div className="border-t border-stone-100 px-3 py-2" data-testid={`notebook-manifest-${n.id}`}>
                    <div className="grid gap-x-6 gap-y-0.5 sm:grid-cols-2">
                      {(n.policies || []).map((p) => (
                        <p key={p.id} className="text-xs text-stone-600">
                          <span className="font-medium text-stone-800">{p.title}</span>
                          <span className="text-stone-400"> — v{p.version}{p.updated_at ? ` · updated ${p.updated_at}` : ""}</span>
                        </p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {myDue.length > 0 && (
        <div className="mb-5 rounded-lg border border-[#B7791F]/30 bg-[#B7791F]/5 p-4" data-testid="policy-due-banner">
          <p className="text-sm font-semibold text-[#8a5d14]">
            {myDue.length} {myDue.length === 1 ? "policy needs" : "policies need"} your acknowledgment
          </p>
          <ul className="mt-1.5 space-y-1">
            {myDue.map((p) => (
              <li key={p.id} className="flex items-center gap-2 text-xs text-stone-600" data-testid={`policy-due-${p.id}`}>
                <span className={p.my_signoff_status === "stale" ? "font-medium text-[#8a5d14]" : "font-medium text-[#C0392B]"}>
                  {p.my_signoff_status === "stale" ? "Updated — re-sign" : "New"}
                </span>
                {p.title}
                <button onClick={() => openViewing(p)} className="font-medium text-primary underline" data-testid={`policy-due-open-${p.id}`}>Read &amp; sign</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {isAdmin && required && (
        <div className="mb-5 rounded-lg border border-stone-200 bg-white p-4 shadow-sm" data-testid="required-coverage-panel">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-sm font-semibold text-stone-800">Required policy coverage</p>
              <p className="mt-0.5 text-xs text-stone-400">Policies a PA non-medical home care agency is expected to have — with who requires each: state (DOH 28 Pa. Code), payer (UPMC/CHC), federal, or HR standard.</p>
            </div>
            <span className="text-xs font-medium text-stone-500" data-testid="required-coverage-count">
              {required.filter((r) => r.status === "covered").length}/{required.length} covered
            </span>
          </div>
          <div className="mt-3 grid gap-1.5">
            {required.map((r) => (
              <div key={r.key} className="flex flex-wrap items-center gap-2 rounded-md border border-stone-100 px-3 py-2" data-testid={`required-${r.key}`}>
                <span className={`h-2 w-2 shrink-0 rounded-full ${r.status === "covered" ? "bg-[#27AE60]" : r.status === "draft" ? "bg-[#B7791F]" : "bg-[#C0392B]"}`} />
                <span className="min-w-0 flex-1 text-xs font-medium text-stone-700">{r.title}</span>
                <span className="flex flex-wrap gap-1">
                  {r.required_by.map((b) => (
                    <span key={b} className="rounded-full bg-stone-100 px-1.5 py-0.5 text-[10px] font-semibold text-stone-500" data-testid={`required-${r.key}-badge-${b.replace(/[^A-Za-z0-9]+/g, "-").toLowerCase()}`}>{b}</span>
                  ))}
                </span>
                {r.status === "covered" && r.policy_id ? (
                  <span className="text-[10px] font-medium text-[#1e8449]" data-testid={`required-${r.key}-status`}>Covered{r.matched_title ? ` — ${r.matched_title}` : ""}</span>
                ) : r.status === "draft" ? (
                  <span className="text-[10px] font-medium text-[#8a5d14]" data-testid={`required-${r.key}-status`}>Draft exists — publish it</span>
                ) : (
                  <button onClick={() => createRequired(r)} disabled={busy === `req-${r.key}`}
                    className="text-[10px] font-semibold text-[#C0392B] underline" data-testid={`required-${r.key}-create`}>
                    {busy === `req-${r.key}` ? "Creating…" : "Missing — create draft"}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mb-5 rounded-lg border border-stone-200 bg-white p-4 shadow-sm" data-testid="policy-qa-box">
        <p className="text-sm font-semibold text-stone-800">Ask a policy question</p>
        <p className="mt-0.5 text-xs text-stone-400">Answers come only from published agency policies.</p>
        <div className="mt-2 flex gap-2">
          <Input value={qaQuestion} onChange={(e) => setQaQuestion(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") askPolicy(); }}
            placeholder="e.g. How much notice do we give before ending services?"
            className="border-stone-300 bg-white" data-testid="policy-qa-input" />
          <Button onClick={askPolicy} disabled={qaBusy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="policy-qa-ask">
            {qaBusy ? "Thinking..." : "Ask"}
          </Button>
        </div>
        {qaAnswer && (
          <div className="mt-3 rounded-md border border-stone-200 bg-accent/40 p-3" data-testid="policy-qa-answer">
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-stone-700">{qaAnswer}</p>
          </div>
        )}
      </div>

      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="relative min-w-56 flex-1">
          <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search policies..."
            className="border-stone-300 bg-white pl-9" data-testid="policy-search-input" />
        </div>
        {isAdmin && (
          <div className="flex gap-1" data-testid="policy-status-tabs">
            {[["published", "Published"], ["draft", "Drafts"], ["archived", "Archived"], ["all", "All"]].map(([key, label]) => (
              <button key={key} onClick={() => setTab(key)}
                className={`rounded-full border px-3 py-1.5 text-xs font-medium ${tab === key ? "border-stone-900 bg-stone-900 text-white" : "border-stone-300 bg-white text-stone-600 hover:bg-accent"}`}
                data-testid={`policy-tab-${key}`}>
                {label} ({counts[key] || 0})
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-3" data-testid="policies-list">
        {visible.length === 0 && (
          <p className="rounded-lg border border-dashed border-stone-300 bg-white p-8 text-center text-sm text-stone-500">
            {query ? "No policies match your search." : isAdmin ? "Nothing here yet — create a policy or import a document." : "No published policies yet."}
          </p>
        )}
        {visible.map((p) => {
          const st = STATUS_STYLES[p.status] || STATUS_STYLES.draft;
          const mine = MY_SIGNOFF[p.my_signoff_status];
          return (
            <div key={p.id} className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid={`policy-${p.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1 cursor-pointer" onClick={() => openViewing(p)}>
                  <p className="font-heading text-sm font-semibold text-primary hover:underline" data-testid={`policy-title-${p.id}`}>{p.title}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${st.cls}`} data-testid={`policy-status-${p.id}`}>{st.label}</span>
                    {p.category && <span className="rounded-full bg-accent px-2 py-0.5 text-[10px] font-medium text-accent-foreground">{p.category}</span>}
                    {(p.tags || []).map((t) => (
                      <span key={t} className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${t.startsWith("DOH") ? "border-[#C0392B]/40 bg-[#C0392B]/10 text-[#C0392B]" : t.startsWith("Keystone") ? "border-[#6a3e97]/40 bg-[#6a3e97]/10 text-[#6a3e97]" : "border-stone-300 bg-stone-50 text-stone-500"}`} data-testid={`policy-tag-${p.id}-${t.replace(/\W+/g, "-")}`}>{t}</span>
                    ))}
                    <span className="text-[10px] text-stone-400">v{p.version} · updated {String(p.updated_at || p.created_at).slice(0, 10)}</span>
                    {(p.aligned_sources || []).slice(0, 3).map((s, i) => (
                      <span key={i} className="rounded-full border border-primary/30 bg-accent px-2 py-0.5 text-[10px] font-semibold text-accent-foreground" title={s.title} data-testid={`policy-src-badge-${p.id}-${i}`}>
                        {s.authority || s.title}
                      </span>
                    ))}
                    {(p.aligned_sources || []).length > 3 && (
                      <span className="text-[10px] text-stone-400" title={p.aligned_sources.map((s) => s.title).join("; ")} data-testid={`policy-src-more-${p.id}`}>+{p.aligned_sources.length - 3} sources</span>
                    )}
                    {p.require_signoff && (
                      <span className="rounded-full border border-primary/30 bg-accent px-2 py-0.5 text-[10px] font-medium text-accent-foreground" data-testid={`policy-signoff-req-${p.id}`}>
                        Sign-off required{p.quiz_count ? ` · quiz (${p.quiz_count})` : ""}
                      </span>
                    )}
                    {!isAdmin && mine && (
                      <span className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${mine.cls}`} data-testid={`policy-my-signoff-${p.id}`}>{mine.label}</span>
                    )}
                    {isAdmin && p.require_signoff && (
                      <span className="text-[10px] text-stone-400" data-testid={`policy-signed-count-${p.id}`}>{p.signoff_current_count || 0} signed (v{p.signoff_version_required}+)</span>
                    )}
                  </div>
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-2">
                  <button onClick={() => openViewing(p)}
                    className="inline-flex h-8 items-center rounded-md bg-primary px-3.5 text-xs font-semibold text-primary-foreground hover:bg-stone-900"
                    data-testid={`policy-view-${p.id}`}>Read policy →</button>
                  {isAdmin && (
                    <>
                      <button onClick={() => setEditing({ id: p.id, title: p.title, content: p.content, category: p.category || "", tags: (p.tags || []).join(", "), version: p.version, reattest: false, require_signoff: !!p.require_signoff })}
                        className="text-xs font-medium text-stone-500 hover:underline" data-testid={`policy-edit-${p.id}`}>Edit</button>
                      <button onClick={() => toggleHistory(p)}
                        className="text-xs font-medium text-stone-500 hover:underline" data-testid={`policy-history-${p.id}`}>History</button>
                      <button onClick={() => openSettings(p)}
                        className="text-xs font-medium text-stone-500 hover:underline" data-testid={`policy-signoff-settings-${p.id}`}>Sign-off</button>
                      {p.require_signoff && (
                        <button onClick={() => openRoster(p)}
                          className="text-xs font-medium text-stone-500 hover:underline" data-testid={`policy-roster-${p.id}`}>Roster</button>
                      )}
                      {p.status !== "published" && (
                        <button onClick={() => setStatus(p, "published")} disabled={busy}
                          className="text-xs font-medium text-[#1e8449] hover:underline" data-testid={`policy-publish-${p.id}`}>Publish</button>
                      )}
                      {p.status === "published" && (
                        <button onClick={() => setStatus(p, "draft")} disabled={busy}
                          className="text-xs font-medium text-stone-500 hover:underline" data-testid={`policy-unpublish-${p.id}`}>Unpublish</button>
                      )}
                      {p.status !== "archived" && (
                        <button onClick={() => setStatus(p, "archived")} disabled={busy}
                          className="text-xs font-medium text-[#8a5d14] hover:underline" data-testid={`policy-archive-${p.id}`}>Archive</button>
                      )}
                      <button onClick={() => remove(p)} className="text-xs font-medium text-[#C0392B] hover:underline" data-testid={`policy-delete-${p.id}`}>Delete</button>
                    </>
                  )}
                </div>
              </div>
              {historyFor === p.id && (
                <div className="mt-3 rounded-md border border-stone-200 bg-stone-50 p-3" data-testid={`policy-versions-${p.id}`}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">Version history — previous versions are snapshotted automatically before any edit or AI fix</p>
                  {versions.length === 0
                    ? <p className="mt-1 text-xs text-stone-400">No prior versions recorded — snapshots start from the next edit.</p>
                    : (
                      <ul className="mt-1 space-y-1">
                        {versions.map((v) => (
                          <li key={v.id} className="text-xs text-stone-600" data-testid={`policy-version-row-${v.id}`}>
                            <span className="font-semibold">v{v.version}</span> — saved {new Date(v.saved_at).toLocaleString()} by {v.saved_by}{v.note ? ` (${v.note})` : ""}
                          </li>
                        ))}
                      </ul>
                    )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {settingsFor && settings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="policy-settings-modal">
          <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
            <h2 className="font-heading text-lg font-semibold text-stone-900">Sign-off &amp; quiz — {settingsFor.title}</h2>
            <p className="mt-1 text-xs text-stone-500">The signed submission is the proof. Completion-based — no pass/fail score.</p>
            <div className="mt-4 space-y-4">
              <label className="flex items-center gap-2 text-sm text-stone-700">
                <input type="checkbox" checked={settings.require_signoff}
                  onChange={(e) => setSettings({ ...settings, require_signoff: e.target.checked })} data-testid="settings-require-signoff" />
                Require staff to read and sign this policy
              </label>
              {settings.require_signoff && (
                <>
                  <label className="flex items-center gap-2 text-sm text-stone-700">
                    <input type="checkbox" checked={settings.include_in_orientation}
                      onChange={(e) => setSettings({ ...settings, include_in_orientation: e.target.checked })} data-testid="settings-orientation" />
                    Part of orientation — new hires acknowledge during onboarding
                  </label>
                  <div className="rounded-md border border-stone-200 p-3" data-testid="settings-assignment">
                    <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">Who must sign</p>
                    <label className="mt-2 flex items-center gap-2 text-sm text-stone-700">
                      <input type="radio" checked={settings.assigned_all} onChange={() => setSettings({ ...settings, assigned_all: true })} data-testid="settings-assign-all" />
                      All staff
                    </label>
                    <label className="mt-1.5 flex items-center gap-2 text-sm text-stone-700">
                      <input type="radio" checked={!settings.assigned_all} onChange={() => setSettings({ ...settings, assigned_all: false })} data-testid="settings-assign-selected" />
                      Selected staff only
                    </label>
                    {!settings.assigned_all && (
                      <div className="mt-2 max-h-40 space-y-1 overflow-y-auto rounded border border-stone-100 bg-stone-50 p-2" data-testid="settings-staff-list">
                        {staff.map((u) => (
                          <label key={u.id} className="flex items-center gap-2 text-xs text-stone-600">
                            <input type="checkbox" checked={settings.assigned_ids.includes(u.id)}
                              onChange={(e) => setSettings((s) => ({
                                ...s,
                                assigned_ids: e.target.checked ? [...s.assigned_ids, u.id] : s.assigned_ids.filter((x) => x !== u.id),
                              }))} data-testid={`settings-staff-${u.id}`} />
                            {u.name || u.email}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="rounded-md border border-stone-200 p-3" data-testid="settings-quiz">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">Comprehension quiz ({settings.quiz.length} questions)</p>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={generateQuiz} disabled={settingsBusy === "gen"}
                          className="border-primary/40 text-xs text-accent-foreground" data-testid="settings-quiz-generate">
                          <Sparkle size={13} className="mr-1" /> {settingsBusy === "gen" ? "Generating…" : "Generate with AI"}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setSettings((s) => ({ ...s, quiz: [...s.quiz, emptyQuestion()] }))}
                          className="border-stone-300 text-xs" data-testid="settings-quiz-add">
                          <Plus size={13} className="mr-1" /> Add
                        </Button>
                      </div>
                    </div>
                    <div className="mt-3 space-y-3">
                      {settings.quiz.map((q, i) => (
                        <div key={`${i}-${(q.q || "").slice(0, 24)}`} className="rounded-md border border-stone-100 bg-stone-50 p-3" data-testid={`settings-quiz-q-${i}`}>
                          <div className="flex items-start justify-between gap-2">
                            <Input value={q.q} onChange={(e) => setQuestion(i, { q: e.target.value })} placeholder={`Question ${i + 1}`}
                              className="border-stone-300 bg-white text-xs" data-testid={`settings-quiz-q-${i}-text`} />
                            <button onClick={() => setSettings((s) => ({ ...s, quiz: s.quiz.filter((_, idx) => idx !== i) }))}
                              className="mt-1.5 text-stone-300 hover:text-[#C0392B]" data-testid={`settings-quiz-q-${i}-del`}><Trash size={14} /></button>
                          </div>
                          <div className="mt-2 space-y-1.5">
                            {q.choices.map((c, j) => (
                              <div key={`${i}-${j}-${(c || "").slice(0, 16)}`} className="flex items-center gap-2">
                                <input type="radio" name={`ans-${i}`} checked={q.answer_index === j}
                                  onChange={() => setQuestion(i, { answer_index: j })} title="Mark as correct answer"
                                  data-testid={`settings-quiz-q-${i}-ans-${j}`} />
                                <Input value={c} onChange={(e) => setQuestion(i, { choices: q.choices.map((x, idx) => (idx === j ? e.target.value : x)) })}
                                  placeholder={`Choice ${j + 1}`} className="h-7 border-stone-300 bg-white text-xs" data-testid={`settings-quiz-q-${i}-choice-${j}`} />
                              </div>
                            ))}
                          </div>
                          <Input value={q.explanation || ""} onChange={(e) => setQuestion(i, { explanation: e.target.value })}
                            placeholder="Explanation shown after signing (optional)"
                            className="mt-2 h-7 border-stone-300 bg-white text-xs" data-testid={`settings-quiz-q-${i}-expl`} />
                        </div>
                      ))}
                      {settings.quiz.length === 0 && <p className="text-xs text-stone-400">No quiz — staff will just read and sign.</p>}
                    </div>
                  </div>
                </>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setSettingsFor(null)} className="border-stone-300 text-stone-500" data-testid="settings-cancel">Cancel</Button>
                <Button onClick={saveSettings} disabled={settingsBusy === "save"} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="settings-save">
                  {settingsBusy === "save" ? "Saving…" : "Save settings"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {rosterFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="policy-roster-modal">
          <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
            <h2 className="font-heading text-lg font-semibold text-stone-900">Sign-off roster — {rosterFor.title}</h2>
            {!roster ? (
              <p className="mt-4 text-sm text-stone-400">Loading…</p>
            ) : (
              <div className="mt-4 space-y-4">
                <p className="text-xs text-stone-500">Current required version: v{roster.signoff_version_required} · {roster.assigned_to?.includes("all") || !roster.assigned_to?.length ? "Assigned to all staff" : `${roster.assigned_to.length} selected staff`}</p>
                <div data-testid="roster-current">
                  <p className="text-xs font-semibold uppercase tracking-wide text-[#1e8449]">Signed current version ({roster.signed_current.length})</p>
                  <ul className="mt-1 space-y-1">
                    {roster.signed_current.map((s) => (
                      <li key={s.email} className="text-xs text-stone-600" data-testid={`roster-signed-${s.email}`}>
                        {s.name} — v{s.policy_version} · {String(s.signed_at).slice(0, 10)}{s.quiz_total ? ` · quiz ${s.quiz_correct}/${s.quiz_total}` : ""}
                      </li>
                    ))}
                    {roster.signed_current.length === 0 && <li className="text-xs text-stone-400">None yet</li>}
                  </ul>
                </div>
                <div data-testid="roster-stale">
                  <p className="text-xs font-semibold uppercase tracking-wide text-[#8a5d14]">Signed an older version ({roster.stale.length})</p>
                  <ul className="mt-1 space-y-1">
                    {roster.stale.map((s) => (
                      <li key={s.email} className="text-xs text-stone-600" data-testid={`roster-stale-${s.email}`}>
                        {s.name} — signed v{s.policy_version} · {String(s.signed_at).slice(0, 10)} — re-sign needed
                      </li>
                    ))}
                    {roster.stale.length === 0 && <li className="text-xs text-stone-400">None</li>}
                  </ul>
                </div>
                <div data-testid="roster-missing">
                  <p className="text-xs font-semibold uppercase tracking-wide text-[#C0392B]">Not signed ({roster.missing.length})</p>
                  <ul className="mt-1 space-y-1">
                    {roster.missing.map((s) => (
                      <li key={s.email} className="text-xs text-stone-600" data-testid={`roster-missing-${s.email}`}>{s.name}</li>
                    ))}
                    {roster.missing.length === 0 && <li className="text-xs text-stone-400">Everyone assigned has signed</li>}
                  </ul>
                </div>
              </div>
            )}
            <div className="mt-4 flex justify-end">
              <Button variant="outline" onClick={() => setRosterFor(null)} className="border-stone-300 text-stone-500" data-testid="roster-close">Close</Button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="policy-edit-modal">
          <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
            <h2 className="font-heading text-lg font-semibold text-stone-900">{editing.id ? "Edit policy" : "New policy"}</h2>
            {editing.id && <p className="mt-1 text-xs text-[#B7791F]">Changing the content bumps the version number.</p>}
            <div className="mt-4 space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="space-y-1.5">
                  <Label>Title</Label>
                  <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                    className="border-stone-300 bg-white" data-testid="policy-title-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>Category</Label>
                  <Input value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                    placeholder="e.g. HR & Operations" className="border-stone-300 bg-white" data-testid="policy-category-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>Tags <span className="text-xs font-normal text-stone-400">comma-separated — e.g. Keystone audit 2026, DOH survey 2025</span></Label>
                  <Input value={editing.tags || ""} onChange={(e) => setEditing({ ...editing, tags: e.target.value })}
                    placeholder="purpose / project tags" className="border-stone-300 bg-white" data-testid="policy-tags-input" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Policy content</Label>
                <Textarea rows={14} value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })}
                  className="border-stone-300 bg-white text-sm leading-relaxed" data-testid="policy-content-input" />
              </div>
              {editing.id && editing.require_signoff && (
                <label className="flex items-center gap-2 text-sm text-stone-700" data-testid="policy-reattest-label">
                  <input type="checkbox" checked={!!editing.reattest} onChange={(e) => setEditing({ ...editing, reattest: e.target.checked })} data-testid="policy-reattest-check" />
                  Require staff to re-acknowledge this change
                  <span className="text-xs text-stone-400">(uncheck for minor edits that don't need reinforcement)</span>
                </label>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditing(null)} className="border-stone-300 text-stone-500" data-testid="policy-edit-cancel">Cancel</Button>
                <Button onClick={save} disabled={busy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="policy-save">
                  {busy ? "Saving..." : "Save Policy"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {importOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="policy-import-modal">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h2 className="font-heading text-lg font-semibold text-stone-900">Import a policy document</h2>
            <p className="mt-1 text-xs text-stone-500">
              Upload one policy document (PDF, Word, or text). Its text is extracted and saved as a draft policy you can review, edit, and publish.
            </p>
            <label className="mt-4 flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed border-stone-300 bg-accent/40 p-8 text-center hover:border-primary">
              <FileArrowUp size={28} className="text-stone-400" />
              <span className="text-sm font-medium text-stone-700">{importFile ? importFile.name : "Choose a PDF, Word, or text file"}</span>
              <span className="text-xs text-stone-400">Scans/photos of paper aren't supported — the file needs real text</span>
              <input type="file" accept=".pdf,.docx,.txt" className="hidden" data-testid="policy-import-file-input"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)} />
            </label>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setImportOpen(false); setImportFile(null); }} className="border-stone-300 text-stone-500" data-testid="policy-import-cancel">Cancel</Button>
              <Button onClick={doImport} disabled={busy || !importFile} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="policy-import-submit">
                {busy ? "Importing..." : "Import as Draft"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
